import { ym } from 'react-metrika';

import { OperatorType } from '@/api/enums/Operators.ts';
import {
    CategoriesRes,
    GetPrettyNumbersParams,
    MonthlySalesOperation,
    Operation,
    StandardResponseItem,
    TariffResponseItem,
} from '@/api/types';
import { MainConfig } from '@/config/MainConfig.ts';
import { isProd } from '@/env.ts';
import { DropdownOptions, OperatorsWithStatuses } from '@/types';

declare global {
    interface Navigator {
        userAgentData?: {
            mobile?: boolean;
        };
    }
}

export function getEnumKeyByValue(enumObj: object, value: number): string | undefined {
    return Object.keys(enumObj).find(key => enumObj[key as keyof typeof enumObj] === value);
}

export function formatNumber(number: string, fullNumber = false) {
    const formattedNumber = number.replaceAll(/\D/gi, '');
    return Number(formattedNumber.substring(fullNumber ? 0 : 1, formattedNumber.length));
}

export function getEnumKeys(enumObj: object) {
    return Object.keys(enumObj).filter(key => isNaN(Number(key)));
}

export function getSearchParamsFromArray(paramName: string, array: (string | number)[]): string {
    return array.reduce<string>(
        (paramStr, currentVal, i) =>
            paramStr +
            `search[${paramName}]${paramName === 'device' ? '' : '[]'}=${currentVal}${i === array.length - 1 ? '' : '&'}`,
        ''
    );
}

export function getSearchParams(
    paramObj: Partial<GetPrettyNumbersParams['search'] & { device: number[] }>
) {
    return Object.entries(paramObj || {}).reduce((paramStr, [key, val], i, array) => {
        if (Array.isArray(val)) {
            if (key !== 'device') {
                return (
                    paramStr +
                    (val.length > 0 ? getSearchParamsFromArray(key, val) : '') +
                    (i === array.length - 1 || val.length === 0 ? '' : '&')
                );
            }

            return (
                paramStr +
                (val.length === 1
                    ? `search[${key}]=${val}${i === array.length - 1 ? '' : '&'}`
                    : '')
            );
        }

        return paramStr + (val ? `search[${key}]=${val}${i === array.length - 1 ? '' : '&'}` : '');
    }, '');
}

export function getPrefixOptions(prefixes: number[]): DropdownOptions<number> {
    return prefixes.map(prefix => ({
        value: prefix,
        label: String(prefix),
    }));
}

export function getOperatorsIconsNames(operators: StandardResponseItem[]): OperatorsWithStatuses {
    return operators.map(operator => ({
        id: operator.id,
        name: getEnumKeyByValue(OperatorType, operator.id) ?? '',
        active: false,
    }));
}

export function getDropdownOptions(resArray: StandardResponseItem[]): DropdownOptions<number> {
    return resArray.map(item => ({
        value: item.id,
        label: item.name,
    }));
}

export function sortRegions(regions: DropdownOptions<number>) {
    return regions.sort((a, b) => {
        const priority = { Москва: 0, 'Санкт-Петербург': 1 };

        const priorityA = priority[a.label as keyof typeof priority] ?? 2;
        const priorityB = priority[b.label as keyof typeof priority] ?? 2;

        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }

        return a.label.localeCompare(b.label);
    });
}

export function getTariffsOptions(
    tariffs: TariffResponseItem[],
    operators: StandardResponseItem[]
) {
    return operators.map(operator => ({
        label: operator.name + ' мин / Гб',
        options: tariffs
            .filter(tariff => tariff.operatorId === operator.id)
            .map(tariff => ({
                value: tariff.sid,
                label: tariff.name.replace('&infin;', '∞'),
            })),
    }));
}

const wordEndings = {
    а: [2, 3, 4],
    ов: [0, 11, 12, 13, 14, 5, 6, 7, 8, 9],
};

export function getCorrectMultipleWord(word: string, number: number): string {
    for (const ending in wordEndings) {
        const nums = wordEndings[ending as keyof typeof wordEndings];

        const lastDigit = number % 10;
        const lastTwoDigits = number % 100;

        if (
            (nums.includes(lastDigit) && !(lastTwoDigits >= 11 && lastTwoDigits <= 14)) ||
            nums.includes(lastTwoDigits)
        ) {
            return word + ending;
        }
    }

    return word;
}

export function joinDigits(digits: string[]) {
    return digits.map(digit => (digit === '' ? '*' : digit)).join('');
}

export function getTotalActiveFilters(
    searchParams: Partial<
        Omit<GetPrettyNumbersParams['search'], 'prefix' | 'msisdnMask' | 'isFree'>
    >
) {
    const searchParamsWithPrice = {
        ...searchParams,
        priceMin: undefined,
        priceMax: undefined,
        price: searchParams.priceMin || searchParams.priceMax,
    };
    const values = Object.values(searchParamsWithPrice);
    const filteredValues = values.filter(value =>
        Array.isArray(value) ? value.length > 0 : !!value
    );
    return filteredValues.reduce(
        (acc, currVal) => acc + (Array.isArray(currVal) ? currVal.length : 1),
        0
    );
}

export function convertPrice(price: string) {
    const parsedPrice = parseInt(price.replaceAll(' ', ''));
    return price ? parsedPrice.toLocaleString('ru-RU') : price;
}

export function filterCategories(
    categoriesData: CategoriesRes[] | undefined,
    categories: number[]
) {
    return categoriesData?.filter(category => categories.includes(category.id));
}

export function getRemainingSeconds(timestamp: number | string) {
    const zeros = '0'.repeat(Date.now().toString().length - String(timestamp).length);

    return Math.ceil((Number(timestamp + zeros) - Date.now()) / 1000);
}

export function formatNumberToStr(phoneNum: number) {
    const phoneStr = phoneNum.toString();

    return `${phoneStr.slice(0, 3)}-${phoneStr.slice(3, 6)}-${phoneStr.slice(6, 8)}-${phoneStr.slice(8)}`;
}

export function formatNumberToFullStr(phoneNum: number) {
    const phoneStr = phoneNum.toString();

    return `+7 (${phoneStr.slice(0, 3)}) ${phoneStr.slice(3, 6)} ${phoneStr.slice(6, 8)}-${phoneStr.slice(8)}`;
}

export function formatTime(seconds: number) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const paddedHours = String(hours).padStart(1, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedSeconds = String(secs).padStart(2, '0');

    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
}

export function getLocalDate(timestamp: number | string, timeZone = 'Europe/Moscow') {
    const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
    const formatter = new Intl.DateTimeFormat('ru-RU', {
        day: 'numeric',
        month: 'long',
        timeZone: timeZone,
    });

    return formatter.format(date);
}

export function groupByDate(operations: Operation[], timeZone = 'Europe/Moscow') {
    const sortedOperations = operations.toSorted((a, b) => b.date - a.date);

    return sortedOperations.reduce((acc: Record<string, Operation[]>, operation) => {
        const formattedDate = getLocalDate(operation.date, timeZone);

        if (!acc[formattedDate]) {
            acc[formattedDate] = [];
        }

        acc[formattedDate].push(operation);
        return acc;
    }, {});
}

export function capitalize(word: string) {
    return word.charAt(0).toUpperCase() + word.slice(1);
}

export function getLastSixMonths() {
    const months = [];
    const formatter = new Intl.DateTimeFormat('ru-RU', { month: 'short' });

    for (let i = 0; i < 6; i++) {
        const date = new Date();
        date.setMonth(date.getMonth() - i, 1);
        const month = formatter.format(date);
        months.unshift(capitalize(month.replaceAll('.', '')));
    }

    return months;
}

export function getLastSixMonthsNumeric() {
    const result = [];
    const date = new Date();

    for (let i = 0; i < 6; i++) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Ensure two digits
        result.push(`${year}-${month}`);

        date.setMonth(date.getMonth() - 1); // Move to the previous month
    }

    return result;
}

export function groupChartData(data: Record<string, MonthlySalesOperation> | []) {
    const emptyBarData = {
        emptyBarData: 0,
        friendsSalesSum: null,
        mySalesSum: null,
        passiveIncome: null,
    };

    const lastSixMonths = getLastSixMonthsNumeric();

    const sortedData = Object.entries(data).sort(
        ([a], [b]) => new Date(b).getTime() - new Date(a).getTime()
    );

    const mappedData: Array<[string, MonthlySalesOperation | typeof emptyBarData]> =
        lastSixMonths.map(date => {
            const targetDateData = sortedData.find(([key]) => key === date);
            return [date, targetDateData ? targetDateData[1] : emptyBarData];
        });

    const formattedData = mappedData.map(([_key, obj]) => {
        for (const key in obj) {
            const typedKey = key as keyof typeof obj;
            if (obj[typedKey] === 0 && key !== 'emptyBarData') {
                obj[typedKey] = null;
            }

            if (!('emptyBarData' in obj)) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                obj.emptyBarData = null;
            }
        }

        return obj;
    });

    if (formattedData.length < 6) {
        const diff = 6 - formattedData.length;
        const emptyBarDataArr = new Array(diff).fill(emptyBarData);
        formattedData.push(...emptyBarDataArr);

        for (const item of formattedData) {
            if (!('emptyBarData' in item)) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                item.emptyBarData = 0;
            }
        }
    }

    return formattedData
        .toReversed()
        .reduce((acc: Record<string, Array<number | null | undefined>>, obj) => {
            for (const key in obj) {
                if (!acc[key]) {
                    acc[key] = [];
                }

                acc[key].push(obj[key as keyof typeof obj]);
            }
            return acc;
        }, {});
}

export function getMonth(isLong = true, nextMonth = 0, day?: number) {
    const formatter = new Intl.DateTimeFormat('ru-RU', {
        day: day === undefined ? undefined : 'numeric',
        month: isLong ? 'long' : 'short',
    });
    const date = new Date();

    date.setMonth(date.getMonth() + nextMonth, 1);

    if (day !== undefined) {
        date.setDate(day);
    }

    return formatter.format(date);
}

export function isMobileCheck() {
    if (navigator.userAgentData?.mobile !== undefined) {
        return navigator.userAgentData.mobile;
    }
    return (
        /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS|BlackBerry|Opera Mini|IEMobile/i.test(
            navigator.userAgent
        ) || matchMedia('(max-width: 768px)').matches
    );
}

export function formatMonthYear(date: Date, capitalized = true) {
    const formattedDate = new Intl.DateTimeFormat('ru-RU', {
        month: 'long',
        year: 'numeric',
    })
        .format(date)
        .replace(' г.', '');

    return capitalized ? capitalize(formattedDate) : formattedDate;
}

export function getCurrentYearMonth() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');

    return `${year}-${month}`;
}

export function getDateOptions(start: string, end: string): DropdownOptions<string> {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const months = [];

    while (startDate <= endDate) {
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        months.unshift(`${year}-${month}`);
        startDate.setMonth(startDate.getMonth() + 1);
    }

    return months.map(month => ({
        value: month,
        label: formatMonthYear(new Date(month)),
    }));
}

/**
 * Проверить использует ли пользователь
 * андройд приложение
 */
export function isAndroidApp() {
    return window.AndroidApp !== undefined;
}

/**
 * Проверить использует ли пользователь
 * приложение
 */
export function isMobileApp() {
    return window.navigator.userAgent.includes('App-FriendMania');
}

/**
 * Обновляет/применяет куки в андройд приложении.
 * Нужно вызывать при любой установке/изменении cookie
 */
export function tryFlushAndroidCookies() {
    if (isAndroidApp()) {
        window.AndroidApp?.flushCookies();
    }
}

/**
 * Отправляет событие в яндекс метрику
 *
 * @param name
 * @param params
 */
export function sendYMGoal(name: string, params?: Record<string, string>) {
    if (isProd()) {
        ym(MainConfig.metric.yandex.id, 'reachGoal', name, params);
    }
}
